<script lang="ts">
	import Input from '../../lib/Input.svelte';
	import TextArea from '../../lib/TextArea.svelte';
	import Row from '../../lib/Row.svelte';
	import Button from '../../lib/Button.svelte';
	import EmailVerification from '../../lib/EmailVerification.svelte';
	import { type Address, addressStore } from '../../api/address.ts';
	import { getTextColorByParty, organizeProfileIntoKeyValuePairs } from '../../api/elections';
	import {
		getMessageDeliveryOptions,
		onProfileImageError,
		type Match,
		type MessageDeliveryOption,
		directoryStore
	} from '../../api/officials';
	import CaretRightIcon from '../../icons/CaretRightIcon.svelte';
	import {
		checkUserPhone,
		deleteRememberMeCookies,
		postMemberships,
		postRememberMeCookies,
		postUser,
		putUser,
		postValidations,
		sanitizeHonorific,
		resetUser,
		type Message,
		type User,
		type Validation,
		type UserIdentity
	} from '../../api/users';
	import Select from '../../lib/Select.svelte';
	import * as t from '../../translations/Directory.ts';
	import {
		postAdvocacyResponse,
		type AdvocacyResponseContract,
		type AdvocacySubmissionResponse,
		type ConfirmRequest,
		type QuestionnaireItem
	} from '../../api/advocacy.ts';
	import {
		associationIdName,
		convertToQueryStringParams,
		type ValidationMessage,
	} from '../../api/common.ts';
	import { userStore } from '../../api/users.ts';
	import Checkbox from '../../lib/Checkbox.svelte';
	import Alert from '../../lib/Alert.svelte';
	import {pop, push} from "svelte-spa-router";

	let allowedPrefixes: string[] = [];
	let phoneRequired: boolean = false;
	let makeUserSelectPrefix: boolean = false;
	let deliveryOptions: MessageDeliveryOption[];
	let matchedTargetMatch: Match | null;
	const prefixString: string = 'Prefix';
	const phoneString: string = 'Phone';
	let keyValueProfile: { [key: string]: any } | undefined;
	export let params;
	export let getPreviousRoute;
	let message: Message = { subject: '', content: '' };
	let prefixRequiredByTarget: any;
	let errorMessages: ValidationMessage[] = [];
	const defaultMaxSubjectLength = 200;
	const defaultMaxContentLength = 9800;
	let maxSubjectLength = 200;
	let maxContentLength = 9800;
	const minLength = 6;
	let isAwaitingVerification = false;
	
	function load() {
		const profile = $directoryStore.categories
				.map(category => (category ? category : []).profiles.map(profiles => profiles))
				.flat()
				.find(p => p.id == params.id);
		keyValueProfile = organizeProfileIntoKeyValuePairs(profile);
		
		if (!keyValueProfile) {
			throw `error loading directory message form`;
		}
		_getMessageDeliveryOptions();
		message = { subject: '', content: '' };

		checkPrefix();
		checkPhone();
	}

	function buildErrorMessages(messages: string[]) {
		let validationErrorMappings = [
			{
				originalError: 'First Name Required',
				formattedError: {
					message: t.Required_Validation_Message(),
					propertyName: 'givenNames'
				}
			},
			{
				originalError: 'Last Name Required',
				formattedError: {
					message: t.Required_Validation_Message(),
					propertyName: 'surname'
				}
			},
			{
				originalError: 'Prefix Required',
				formattedError: {
					message: t.Required_Validation_Message(),
					propertyName: 'honorific'
				}
			},
			{
				originalError: 'Email required',
				formattedError: {
					message: t.Required_Validation_Message(),
					propertyName: 'email'
				}
			},
			{
				originalError: 'Phone Number mandatory',
				formattedError: {
					message: t.Required_Validation_Message(),
					propertyName: 'phoneNumber'
				}
			},
			{
				originalError: 'Home Address mandatory',
				formattedError: {
					message: `Address ${t.Required_Validation_Message()}`,
					propertyName: ''
				}
			},
			{
				originalError: 'invalid first name',
				formattedError: {
					message: '',
					propertyName: 'givenNames'
				}
			},
			{
				originalError: 'invalid first name',
				formattedError: {
					message: t.Invalid_First_Name_Validation_Message(),
					propertyName: ''
				}
			},
			{
				originalError: 'invalid last name',
				formattedError: {
					message: t.Invalid_Last_Name_Validation_Message(),
					propertyName: ''
				}
			},
			{
				originalError: 'invalid last name',
				formattedError: {
					message: '',
					propertyName: 'surname'
				}
			},
			{
				originalError: 'Invalid prefix',
				formattedError: {
					message: t.Invalid_Value_Validation_Message(),
					propertyName: 'honorific'
				}
			},
			{
				originalError: 'Invalid email address',
				formattedError: {
					message: t.Email_Validation_Message(),
					propertyName: 'email'
				}
			},
			{
				originalError: 'Invalid phone number',
				formattedError: {
					message: t.Phone_Validation_Message(),
					propertyName: 'phoneNumber'
				}
			},
			{
				originalError: 'mobile number is already in use',
				formattedError: {
					message: t.Mobile_Number_In_Use_Validation_Message(),
					propertyName: ''
				}
			},
			{
				originalError: 'mobile number is already in use',
				formattedError: {
					message: '',
					propertyName: 'phoneNumber'
				}
			},
			{
				originalError: 'subject is required',
				formattedError: {
					message: t.Required_Validation_Message(),
					propertyName: 'subject'
				}
			},
			{
				originalError: 'Message body is required',
				formattedError: {
					message: t.Required_Validation_Message(),
					propertyName: 'content'
				}
			},
			{
				originalError: 'Message body is too long',
				formattedError: {
					message: t.Maximum_Length_Validation_Message(maxContentLength.toString()),
					propertyName: 'content'
				}
			},
			{
				originalError: 'Message body is too short',
				formattedError: {
					message: t.Minimum_Length_Validation_Message(minLength.toString()),
					propertyName: 'content'
				}
			},
			{
				originalError: 'Message subject is too long',
				formattedError: {
					message: t.Maximum_Length_Validation_Message(maxSubjectLength.toString()),
					propertyName: 'subject'
				}
			},
			{
				originalError: 'Message subject is too short',
				formattedError: {
					message: t.Minimum_Length_Validation_Message(minLength.toString()),
					propertyName: 'subject'
				}
			}
		];

		let newErrorMessages: ValidationMessage[] = [];

		for (let i = 0; i < messages.length; i++) {
			const message = messages[i];
			const matchingMappings = validationErrorMappings.filter((item) =>
				message.includes(item.originalError)
			);
			if (matchingMappings) {
				newErrorMessages.push(...matchingMappings.map((item) => item.formattedError));
			} else {
				newErrorMessages.push({ message: message, propertyName: '' });
			}
		}
		errorMessages = newErrorMessages;
	}

	async function _getMessageDeliveryOptions() {
		if (keyValueProfile) {
			deliveryOptions = await getMessageDeliveryOptions(`E`, keyValueProfile[`id`]);
			prefixRequiredByTarget = deliveryOptions[0]?.requiredQuestions?.find(
				(x) => x.question.toLowerCase().trim() === prefixString.toLowerCase().trim()
			);

			maxSubjectLength = deliveryOptions[0]?.maximumSubjectLength ?? defaultMaxSubjectLength;
			maxContentLength = deliveryOptions[0]?.maximumMessageLength ?? defaultMaxContentLength;

			if (prefixRequiredByTarget) allowedPrefixes = prefixRequiredByTarget.validAnswers;

			const opt = deliveryOptions[0]?.requiredUserFields.find(
				(ruf) => ruf.toLowerCase().trim() === phoneString.toLowerCase().trim()
			);
			phoneRequired = opt != undefined && opt != null;
		}
	}
	async function handlePrefixBlur() {
		checkPrefix();
	}

	async function checkPrefix() {
		if (
			$userStore.user.honorific == undefined ||
			$userStore.user.honorific == null ||
			($userStore.user.honorific ?? '').trim() == ''
		)
			return;

		try {
			const response = await sanitizeHonorific($userStore.user);
			$userStore.user.honorific = response.honorific;
		} catch (error) {
		} finally {
			makeUserSelectPrefix = allowedPrefixes && allowedPrefixes.length > 0 && !allowedPrefixes.includes($userStore.user.honorific);
		}
	}

	async function handlePhoneBlur() {
		// checkPhone();  //this would be more valid if we put mobile number back in instead of phone
	}

	async function checkPhone() {
		if (
			$userStore.user.phoneNumber == undefined ||
			$userStore.user.phoneNumber == null ||
			($userStore.user.phoneNumber ?? '').trim() == ''
		)
			return;

		const response = await checkUserPhone($userStore.user);
	}

	function validateClientSide() {
		errorMessages = [];
		if (prefixRequiredByTarget) {
			if (makeUserSelectPrefix && !$userStore.user.commonHonorific) {
				errorMessages.push({
					propertyName: 'commonHonorific',
					message: t.Required_Validation_Message()
				});
			} else if (!$userStore.user.honorific) {
				errorMessages.push({ propertyName: 'honorific', message: t.Required_Validation_Message() });
			}
		}
		if (!message.subject) {
			errorMessages.push({ propertyName: 'subject', message: t.Required_Validation_Message() });
		}
		if (!message.content) {
			errorMessages.push({ propertyName: 'content', message: t.Required_Validation_Message() });
		}
		if (!$userStore.user.surname) {
			errorMessages.push({ propertyName: 'surname', message: t.Required_Validation_Message() });
		}
		if (!$userStore.user.givenNames) {
			errorMessages.push({ propertyName: 'givenNames', message: t.Required_Validation_Message() });
		}
		if (!$userStore.user.emailAddress) {
			errorMessages.push({ propertyName: 'email', message: t.Required_Validation_Message() });
		}
		if (phoneRequired && !$userStore.user.phoneNumber) {
			errorMessages.push({ propertyName: 'phoneNumber', message: t.Required_Validation_Message() });
		}
		if (message.subject && message.subject.length > maxSubjectLength) {
			errorMessages.push({
				propertyName: 'subject',
				message: t.Maximum_Length_Validation_Message(maxSubjectLength.toString())
			});
		}
		if (message.content && message.content.length > maxContentLength) {
			errorMessages.push({
				propertyName: 'content',
				message: t.Maximum_Length_Validation_Message(maxContentLength.toString())
			});
		}
	}

	async function handleSendMessageClick(e: MouseEvent): Promise<void> {
		validateClientSide();
		if (errorMessages.length) return;

		const savedAddress = $addressStore.selectedAddress;
		if (savedAddress?.addressType === 'B') {
			$userStore.user.homeAddress = undefined;
			$userStore.user.businessAddress = savedAddress;
		} else {
			$userStore.user.homeAddress = savedAddress;
			$userStore.user.businessAddress = undefined;
		}

		const questionnaire = buildQuestionnaire();
		try {
			const postUserResponse: User = !$userStore.user?.userId ? await postUser($userStore.user) : await putUser($userStore.user);
			$userStore.user.userToken = postUserResponse.userToken;
			$userStore.user.userId = postUserResponse.userId;

			//check response for error

			if (!$userStore.user.userToken || $userStore.user.userToken.trim() === '' || !$userStore.user.userId) {
				throw `error with user`;
			}

			const postMembershipsResponse: any = await postMemberships($userStore.user);
			if ($userStore.user.rememberMe === true) {
				const postCookiesResponse: any = await postRememberMeCookies($userStore.user);
			} else {
				const deleteCookieResponse = await deleteRememberMeCookies();
			}

			const validationsResponse: Validation[] = await postValidations($userStore.user);

			const advocacyResponse: AdvocacyResponseContract = {
				association: associationIdName,
				userId: $userStore.user.userId,
				defaultAddressType: savedAddress.addressType,
				signature: `${$userStore.user.givenNames} ${$userStore.user.surname}`,
				messages: [
					{
						body: message.content,
						subject: message.subject,
						messageId: 0,
						complimentaryClose: t.Sincerely(),
						targets: [
							{
								deliveryMethod: deliveryOptions[0].deliveryMethod,
								id: params.id,
								type: 'E',
								questionnaire: questionnaire
							}
						]
					}
				]
			};

			const messageResponse: AdvocacySubmissionResponse = await postAdvocacyResponse(
				advocacyResponse,
				$userStore.user.userToken
			);

			let confirmRequest: ConfirmRequest = {
				AddressType: savedAddress.addressType,
				checksum: $userStore.user.homeAddress?.checksum!,
				city: $userStore.user.homeAddress?.city!,
				country: $userStore.user.homeAddress?.country!,
				county: $userStore.user.homeAddress?.county!,
				latitude: $userStore.user.homeAddress?.coordinates?.latitude!,
				longitude: $userStore.user.homeAddress?.coordinates?.longitude!,
				state: $userStore.user.homeAddress?.state!,
				streetAddress: $userStore.user.homeAddress?.streetAddress!,
				zipCode: $userStore.user.homeAddress?.zipCode!,
				zipCodeExtension: $userStore.user.homeAddress?.zipCodeExtension!,

				deliveredMessageIds: messageResponse.deliveredMessages[0].deliveredId,
				smIds: 0,
				user: $userStore.user.userToken
			};
			let queryParamString = convertToQueryStringParams(confirmRequest);

			if ($userStore.user.rememberMe === false) {
				resetUser($userStore.user);
				$userStore.user.rememberMe = false;
				makeUserSelectPrefix = false;
			}
			
			push(`/Officials/${params.id}/MessageConfirmation`);
		} catch (error: any) {
			if (error.statusCode === 409) {
				isAwaitingVerification = true;
			} else {
				buildErrorMessages(error.toString().split('\r\n'));
				if (errorMessages.length) return;
			}
		}
	}

	function buildQuestionnaire(): QuestionnaireItem[] {
		let result: QuestionnaireItem[] = [];
		if (prefixRequiredByTarget) {
			result.push({
				question: prefixString,
				answer: makeUserSelectPrefix ? $userStore.user.commonHonorific : $userStore.user.honorific
			});
		}
		if (phoneRequired) {
			result.push({ question: phoneString, answer: $userStore.user.phoneNumber });
		}

		return result;
	}

	async function clearUser() {
		const deleteCookieResponse = await deleteRememberMeCookies();
		resetUser($userStore.user);
		$addressStore = { selectedAddress: undefined, addresses: new Map() };
		makeUserSelectPrefix = false;
		push('/')
	}
	
	function onUserVerified(user: UserIdentity) { 
		$userStore.user.userId = user.id;
		$userStore.user.userToken = user.token;
		handleSendMessageClick();
	}
	load();
</script>

<div class="flex flex-row col-span-6 gap-2">
	<div>
		<Button class="text-xl" type="navigation" on:click={() => getPreviousRoute() === undefined ? push('/Officials/' + params.id) : pop()}>
			<div class="p-3">
				<CaretRightIcon
					style="transform: scaleX(-1);"
					color="var(--vv-action-center-button-color)"
				/>
			</div>
		</Button>
	</div>
	<div class="my-auto">
		<h1 class="text-3xl">{t.Compose_Message_Header()}</h1>
	</div>
</div>
{#if errorMessages.some((item) => item.propertyName === '')}
	<div class="flex flex-row col-span-6 gap-2">
		<Alert type="danger">
			{#each errorMessages
				.filter((item) => item.propertyName === '')
				.map((item) => `${item.message}`) as error}
				<li>{error}</li>
			{/each}
		</Alert>
	</div>
{/if}
<div class="flex flex-col @xl:col-span-3 @xl:grid gap-4 p-4">
	<Row>
		<h2 class="text-xl">{t.Compose_Message_To_SubHeader()}</h2>
		<div class="ml-8">
			<img
				on:error={onProfileImageError}
				class="h-20 object-cover rounded-xl"
				src={keyValueProfile?.photoUrl}
				alt={keyValueProfile?.title + keyValueProfile?.displayName}
			/>
		</div>
		<div class="flex flex-row gap-2">
			<div>
				<h2>
					<span class="text-lg">
						<span class="text-nowrap">{keyValueProfile?.title} </span>
						<span class="text-nowrap">{keyValueProfile?.displayName}</span>
					</span>
				</h2>
				<h3 class="text-base">
					<span class={getTextColorByParty(keyValueProfile?.party)}>{keyValueProfile?.party}</span>
					{#if keyValueProfile?.state}
						<span> - {keyValueProfile?.state}</span>
					{/if}
				</h3>
			</div>
			<div></div>
		</div>
	</Row>
</div>
<div class="py-4 flex flex-col @xl:col-span-3 gap-2">
	<Row class="px-4">
		<h2 class="text-xl">{t.Compose_Message_From_SubHeader()}</h2>
	</Row>
	<div class="bg-gray-100 px-4">
		{#if $userStore.user.userId && $userStore.user.userId > 0}
			<Row class="md:flex-row-reverse">
				<Button type="hyperlink" on:click={clearUser}
					>{t.Logged_In_User_Link_Text($userStore.user.givenNames ?? 'you')}</Button
				>
			</Row>
		{/if}
		<Row>
			<Input
				id="honorific"
				fieldsetClass=""
				bind:value={$userStore.user.honorific}
				on:blur={handlePrefixBlur}
				placeholder={t.Compose_Message_Prefix_Label()}
				errors={errorMessages
					.filter((item) => item.propertyName === 'honorific')
					.map((item) => `${item.message}`)}
			>
				<span slot="label">{t.Compose_Message_Prefix_Label()}</span>
			</Input>
			<Input
				id="givenNames"
				fieldsetClass="w-full"
				placeholder={t.Compose_Message_Firstname_Label()}
				bind:value={$userStore.user.givenNames}
				errors={errorMessages
					.filter((item) => item.propertyName === 'givenNames')
					.map((item) => `${item.message}`)}
			>
				<span slot="label">{t.Compose_Message_Firstname_Label()}</span>
			</Input>
			<Input
				id="surname"
				fieldsetClass="w-full"
				placeholder={t.Compose_Message_Lastname_Label()}
				bind:value={$userStore.user.surname}
				errors={errorMessages
					.filter((item) => item.propertyName === 'surname')
					.map((item) => `${item.message}`)}
			>
				<span slot="label">{t.Compose_Message_Lastname_Label()}</span>
			</Input>
		</Row>
		<Row>
			<Input
				id="email"
				type="email"
				fieldsetClass="w-full"
				placeholder={t.Compose_Message_Email_Label()}
				bind:value={$userStore.user.emailAddress}
				errors={errorMessages
					.filter((item) => item.propertyName === 'email')
					.map((item) => `${item.message}`)}
			>
				<span slot="label">{t.Compose_Message_Email_Label()}</span>
			</Input>
		</Row>
		<Row>
			<Checkbox bind:checked={$userStore.user.membershipReceivesMessages}>
				<span>{t.Membership_Receives_Messages_Label()}</span>
			</Checkbox>
		</Row>
		{#if phoneRequired}
			<Row>
				<Input
					type="tel"
					id="phoneNumber"
					pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
					required
					fieldsetClass="w-full"
					placeholder={t.Compose_Message_Phone_Label()}
					bind:value={$userStore.user.phoneNumber}
					errors={errorMessages
						.filter((item) => item.propertyName === 'phoneNumber')
						.map((item) => `${item.message}`)}
					on:blur={handlePhoneBlur}
				>
					<span slot="label">{t.Compose_Message_Phone_Label()}</span>
				</Input>
			</Row>
		{/if}
		<Row
			><Checkbox bind:checked={$userStore.user.rememberMe}>
				<span>{t.Remember_Me_Label()}</span>
			</Checkbox>
		</Row>
	</div>
</div>
<div class="col-span-6 gap-2">
	{#if makeUserSelectPrefix && allowedPrefixes}
		<Alert type="warning">
			<div>{t.Compose_Message_Specific_Prefix_Message_1()}</div>
			<div>{t.Compose_Message_Specific_Prefix_Message_2()}</div>
		</Alert>
		<Row>
			<Select
				id="commonHonorific"
				fieldsetClass="xl:min-w-48"
				placeholder={t.Compose_Message_Specific_Prefix_Placeholder()}
				bind:value={$userStore.user.commonHonorific}
				errors={errorMessages
					.filter((item) => item.propertyName === 'commonHonorific')
					.map((item) => `${item.message}`)}
			>
				{#each allowedPrefixes as prefixOption}
					<option value={prefixOption} selected={prefixOption === $userStore.user.commonHonorific}
						>{prefixOption}</option
					>
				{/each}
				<div slot="label">{t.Compose_Message_Specific_Prefix_Label()}</div>
			</Select>
		</Row>
	{/if}
	<Alert type="info">{t.Keep_Messages_Polite_Message()}</Alert>
	<Row>
		<Input
			id="subject"
			fieldsetClass="w-full"
			placeholder={t.Compose_Message_Subject_Label()}
			maxlength={maxSubjectLength}
			bind:value={message.subject}
			errors={errorMessages
				.filter((item) => item.propertyName === 'subject')
				.map((item) => `${item.message}`)}
		>
			<span slot="label">{t.Compose_Message_Subject_Label()}</span>
		</Input>
	</Row>
	<Row>
		<TextArea
			id="content"
			fieldsetClass="w-full"
			maxlength={maxContentLength}
			rows="10"
			placeholder={t.Compose_Message_Message_Placeholder()}
			bind:value={message.content}
			errors={errorMessages
				.filter((item) => item.propertyName === 'content')
				.map((item) => `${item.message}`)}
		>
			<span slot="label">{t.Compose_Message_Message_Label()}</span>
		</TextArea>
	</Row>
	<Row>
		<div class="grow">
			<Button
				type="action-center"
				class="float-right inline-flex flex-row w-40 mt-4 justify-center border"
				on:click={handleSendMessageClick}
			>
				{t.Compose_Message_Send_Message_Button_Text()}
			</Button>
		</div>
	</Row>
</div>

<EmailVerification bind:isAwaitingVerification={isAwaitingVerification} 
				   bind:emailAddress={$userStore.user.emailAddress} 
				   onUserVerified={onUserVerified}>
</EmailVerification>
